import { statusCategoryForId } from '@jira/platform__status-categories/src/status-categories.tsx';
import type { Ari } from '@atlassian/jira-platform-ari/src/index.tsx';
import { useQueryParams } from '@atlassian/jira-software-router-utils/src/services/query-params/index.tsx';
import {
	QUERY_PARAM_TEXT,
	QUERY_PARAM_EPIC,
	QUERY_PARAM_STATUS_CATEGORY,
	QUERY_PARAM_WARNINGS,
	QUERY_PARAM_ASSIGNEE,
	QUERY_PARAM_SORT_BY,
	QUERY_PARAM_SORT_ORDER,
} from './constants.tsx';
import type { WarningsCategory, SortBy, SortOrder } from './types.tsx';
import { parseQueryParams, isAnyFilterApplied } from './utils.tsx';

type AccountId = string;
type IssueStatusCategoryId = string;

const joinValuesIfNotEmpty = (values: string[]): string | undefined =>
	values.length ? values.join(',') : undefined;

export const useFilterSortingQueryParams = () => {
	const [queryParams, setQueryParams] = useQueryParams(
		QUERY_PARAM_TEXT,
		QUERY_PARAM_EPIC,
		QUERY_PARAM_STATUS_CATEGORY,
		QUERY_PARAM_WARNINGS,
		QUERY_PARAM_ASSIGNEE,
		QUERY_PARAM_SORT_BY,
		QUERY_PARAM_SORT_ORDER,
	);

	const parsedQueryParams = parseQueryParams(queryParams);

	return {
		parsedQueryParams,
		isAnyFilterApplied: isAnyFilterApplied(parsedQueryParams),
		setTextFilterParam: (searchStr: string | undefined): void =>
			setQueryParams({
				[QUERY_PARAM_TEXT]: searchStr === '' || searchStr == null ? undefined : searchStr,
			}),
		setEpicFilterParam: (issueIds: Ari[]): void =>
			setQueryParams({ [QUERY_PARAM_EPIC]: joinValuesIfNotEmpty(issueIds) }),
		setStatusCategoryFilterParam: (statusCategories: IssueStatusCategoryId[]): void =>
			setQueryParams({
				[QUERY_PARAM_STATUS_CATEGORY]: joinValuesIfNotEmpty(
					statusCategories.map(statusCategoryForId),
				),
			}),
		setWarningsFilterParam: (warnings: WarningsCategory[]): void =>
			setQueryParams({ [QUERY_PARAM_WARNINGS]: joinValuesIfNotEmpty(warnings) }),
		setAssigneeFilterParam: (assignees: AccountId[]): void =>
			setQueryParams({ [QUERY_PARAM_ASSIGNEE]: joinValuesIfNotEmpty(assignees) }),
		setSortByParam: (sortBy: SortBy): void => setQueryParams({ [QUERY_PARAM_SORT_BY]: sortBy }),
		setSortOrderParam: (sortOrder: SortOrder): void =>
			setQueryParams({ [QUERY_PARAM_SORT_ORDER]: sortOrder }),
		clearAll: (): void => {
			setQueryParams({
				[QUERY_PARAM_TEXT]: undefined,
				[QUERY_PARAM_EPIC]: undefined,
				[QUERY_PARAM_STATUS_CATEGORY]: undefined,
				[QUERY_PARAM_WARNINGS]: undefined,
				[QUERY_PARAM_ASSIGNEE]: undefined,
				[QUERY_PARAM_SORT_BY]: undefined,
				[QUERY_PARAM_SORT_ORDER]: undefined,
			});
		},
		showIssuesWithWarnings: (warnings: WarningsCategory[]): void => {
			setQueryParams({
				[QUERY_PARAM_TEXT]: undefined,
				[QUERY_PARAM_EPIC]: undefined,
				[QUERY_PARAM_STATUS_CATEGORY]: undefined,
				[QUERY_PARAM_WARNINGS]: joinValuesIfNotEmpty(warnings),
				[QUERY_PARAM_ASSIGNEE]: undefined,
				[QUERY_PARAM_SORT_BY]: undefined,
				[QUERY_PARAM_SORT_ORDER]: undefined,
			});
		},
		showStatusCategoryIssues: (statusCategories: IssueStatusCategoryId[]): void => {
			setQueryParams({
				[QUERY_PARAM_TEXT]: undefined,
				[QUERY_PARAM_EPIC]: undefined,
				[QUERY_PARAM_STATUS_CATEGORY]: joinValuesIfNotEmpty(
					statusCategories.map(statusCategoryForId),
				),
				[QUERY_PARAM_WARNINGS]: undefined,
				[QUERY_PARAM_ASSIGNEE]: undefined,
				[QUERY_PARAM_SORT_BY]: undefined,
				[QUERY_PARAM_SORT_ORDER]: undefined,
			});
		},
	};
};

export const useSortParams = () => {
	const { parsedQueryParams } = useFilterSortingQueryParams();

	return {
		sortByField: parsedQueryParams[QUERY_PARAM_SORT_BY] ?? 'CREATED',
		sortOrder: parsedQueryParams[QUERY_PARAM_SORT_ORDER] ?? 'ASC',
	};
};
